import { EquipType } from "../models/common";
import { SpriteLoader } from "../sprite-loader";
import { BLUEPRINT_BG, BLUEPRINT_LINE, COLORS, darkenColor, hexToPhaser } from "./colors";

export class ItemSlot {
  rect:
    | Phaser.GameObjects.Rectangle
    | Phaser.GameObjects.Arc
    | Phaser.GameObjects.Graphics
    | null = null;
  x: number;
  y: number;
  size: number = 48; // Standard size for all item slots
  equipType: EquipType | null = null;
  selected: boolean = false;
  hasItem: boolean = false;
  borderRadius: number = 8; // Rounded corner radius
  materialOutlineColor: number | null = null;
  blueprintMode: boolean = false;
  highlightSelectedAlways: boolean = false;

  setMaterialOutlineColor(color: number | null) {
    this.materialOutlineColor = color;
  }

  setBlueprintMode(enabled: boolean) {
    this.blueprintMode = enabled;
  }

  getBorderStyle(): { color: number; width: number } {
    if (this.selected && (this.hasItem || this.highlightSelectedAlways)) {
      return { color: 0xffffff, width: 4 };
    } else if (this.materialOutlineColor !== null) {
      return { color: this.materialOutlineColor, width: 4 };
    } else if (this.blueprintMode) {
      return { color: BLUEPRINT_LINE, width: 4 };
    } else {
      return { color: darkenColor(hexToPhaser(COLORS.FG), 0.3), width: 4 };
    }
  }

  /** Draws a faint blueprint grid inside the slot bounds; caller has already filled the background. */
  private drawBlueprintGrid(graphics: Phaser.GameObjects.Graphics) {
    graphics.lineStyle(1, BLUEPRINT_LINE, 0.35);
    const step = this.size / 4;
    for (let i = 1; i < 4; i++) {
      graphics.lineBetween(i * step, 3, i * step, this.size - 3);
      graphics.lineBetween(3, i * step, this.size - 3, i * step);
    }
  }

  applyBorderStyle() {
    if (this.rect) {
      const { color, width } = this.getBorderStyle();

      // Handle Graphics objects (rounded rectangles)
      if (this.rect instanceof Phaser.GameObjects.Graphics) {
        this.rect.clear();
        this.rect.fillStyle(hexToPhaser(COLORS.FG), 1);
        this.rect.fillRoundedRect(
          0,
          0,
          this.size,
          this.size,
          this.borderRadius,
        );
        this.rect.lineStyle(width, color, 1);
        this.rect.strokeRoundedRect(
          0,
          0,
          this.size,
          this.size,
          this.borderRadius,
        );
      }
      // Handle Rectangle and Circle objects
      else if ("setStrokeStyle" in this.rect) {
        this.rect.setStrokeStyle(width, color, 1);
      }
    }
  }

  constructor(x: number, y: number, hasItem: boolean = false) {
    this.x = x;
    this.y = y;
    this.hasItem = hasItem;
  }

  setSelected(selected: boolean) {
    this.selected = selected;
  }

  setEquipType(equipType: EquipType) {
    this.equipType = equipType;
  }

  setHighlightSelectedAlways(highlight: boolean) {
    this.highlightSelectedAlways = highlight;
  }

  /**
   * Creates a Phaser rounded rectangle for this item slot with standard size and colors
   */
  createRectangle(scene: Phaser.Scene): Phaser.GameObjects.Graphics {
    const graphics = scene.add.graphics();
    graphics.setScrollFactor(0);
    graphics.setPosition(this.x, this.y);

    // Draw filled rounded rectangle (at local 0,0 since position is set)
    graphics.fillStyle(this.blueprintMode ? BLUEPRINT_BG : hexToPhaser(COLORS.FG), 1);
    graphics.fillRoundedRect(
      0,
      0,
      this.size,
      this.size,
      this.borderRadius,
    );

    if (this.blueprintMode) {
      this.drawBlueprintGrid(graphics);
    }

    // Draw border
    const { color, width } = this.getBorderStyle();
    graphics.lineStyle(width, color, 1);
    graphics.strokeRoundedRect(
      0,
      0,
      this.size,
      this.size,
      this.borderRadius,
    );

    // Make graphics interactive with a rectangular hit area
    graphics.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, this.size, this.size),
      Phaser.Geom.Rectangle.Contains,
    );
    graphics.input!.cursor = "pointer";

    this.rect = graphics as any;
    return graphics;
  }

  /**
   * Creates a Phaser circle for this item slot (used for action slots) with standard size and colors
   */
  createCircle(scene: Phaser.Scene): Phaser.GameObjects.Arc {
    const circle = scene.add.circle(
      this.x + this.size / 2,
      this.y + this.size / 2,
      this.size / 2,
      this.blueprintMode ? BLUEPRINT_BG : hexToPhaser(COLORS.FG),
      1,
    );
    circle.setOrigin(0.5);
    circle.setScrollFactor(0);
    // Explicit local-space hit area so the circle remains clickable inside moving containers.
    // With origin(0.5) the bounding box runs (0,0)→(size,size) in local space.
    circle.setInteractive(
      new Phaser.Geom.Circle(this.size / 2, this.size / 2, this.size / 2),
      Phaser.Geom.Circle.Contains,
    );
    this.rect = circle as any;
    this.applyBorderStyle();
    return circle;
  }

  draw(ctx: CanvasRenderingContext2D | null) {
    // Guard against null context during reconnection scenarios
    if (!ctx) return;

    // Draw dark square
    ctx.fillStyle = COLORS.FG;
    ctx.fillRect(this.x, this.y, this.size, this.size);
    // Draw border
    if (this.selected && (this.hasItem || this.highlightSelectedAlways)) {
      ctx.strokeStyle = "#fff"; // white border if selected and has item
      ctx.lineWidth = 3;
    } else {
      ctx.strokeStyle = "#888"; // gray border by default
      ctx.lineWidth = 2;
    }
    ctx.strokeRect(this.x, this.y, this.size, this.size);

    // Draw equipType icon if no item and equipType is set
    if (!this.hasItem && this.equipType) {
      const iconName = `equip-${this.equipType.toLowerCase()}.png`;
      const iconPath = `sprites/icons/${iconName}`;
      const iconImg = SpriteLoader.getImage(iconPath);
      // Only draw image if it exists and context is valid
      if (iconImg && ctx) {
        const iconSize = this.size * 0.6;
        const x = this.x + (this.size - iconSize) / 2;
        const y = this.y + (this.size - iconSize) / 2;
        ctx.drawImage(iconImg, x, y, iconSize, iconSize);
      }
    }
  }
}
